import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Input } from '../../components/ui/Input';
import { Logo } from '../../components/marca/Logo';
import { servicioAutenticacion } from '../../../services/ServicioAutenticacion';
import { useSesionStore } from '../../stores/sesionStore';
import { ErrorApp, registrarError } from '../../../domain/errores';
import estilos from './Login.module.css';

const LARGO_MINIMO = 8;

/** Requisitos que debe cumplir la contraseña, con su estado actual. */
function evaluarRequisitos(clave: string) {
  return [
    { texto: `Al menos ${LARGO_MINIMO} caracteres`, cumple: clave.length >= LARGO_MINIMO },
    { texto: 'Una letra mayúscula', cumple: /[A-ZÁÉÍÓÚÑ]/.test(clave) },
    { texto: 'Una letra minúscula', cumple: /[a-záéíóúñ]/.test(clave) },
    { texto: 'Un número', cumple: /\d/.test(clave) },
  ];
}

/**
 * Cambio de contraseña.
 *
 * Sirve tanto para el cambio obligatorio tras el alta como para el flujo de
 * recuperación por correo, que llega con la sesión ya establecida.
 */
export function CambiarClave() {
  const refrescarPerfil = useSesionStore((s) => s.refrescarPerfil);
  const perfil = useSesionStore((s) => s.perfil);
  const salir = useSesionStore((s) => s.salir);
  const navegar = useNavigate();

  const [clave, setClave] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requisitos = useMemo(() => evaluarRequisitos(clave), [clave]);
  const cumpleTodo = requisitos.every((r) => r.cumple);
  const coinciden = clave.length > 0 && clave === confirmacion;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      await servicioAutenticacion.cambiarClave(clave);
      await refrescarPerfil();
      toast.success('Contraseña actualizada');
      navegar('/', { replace: true });
    } catch (e) {
      registrarError('cambiarClave', e);
      setError(
        e instanceof ErrorApp ? e.message : 'No se pudo cambiar la contraseña. Inténtalo de nuevo.',
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className={estilos.contenedor}>
      <div className={estilos.caja}>
        <div className={estilos.encabezado}>
          <Logo variante="centrado" alto={72} className={estilos.logo} />
          <h1 className={estilos.titulo}>Crea tu contraseña</h1>
          <p className={estilos.subtitulo}>
            {perfil?.debeCambiarClave
              ? 'Por seguridad debes cambiarla antes de continuar.'
              : 'Elige una contraseña nueva para tu cuenta.'}
          </p>
        </div>

        <form className={estilos.formulario} onSubmit={enviar} noValidate>
          {error && (
            <div className={estilos.aviso} role="alert">
              <Icon nombre="errorContorno" tamano={16} />
              <span>{error}</span>
            </div>
          )}

          <Input
            etiqueta="Contraseña nueva"
            alternarVisibilidad
            autoComplete="new-password"
            required
            value={clave}
            onChange={(e) => setClave(e.target.value)}
          />

          <ul className={estilos.requisitos}>
            {requisitos.map((r) => (
              <li
                key={r.texto}
                className={[estilos.requisito, r.cumple ? estilos.requisitoCumplido : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <Icon nombre={r.cumple ? 'exito' : 'circulo'} tamano={13} />
                {r.texto}
              </li>
            ))}
          </ul>

          <Input
            etiqueta="Repite la contraseña"
            alternarVisibilidad
            autoComplete="new-password"
            required
            value={confirmacion}
            onChange={(e) => setConfirmacion(e.target.value)}
            error={confirmacion.length > 0 && !coinciden ? 'Las contraseñas no coinciden' : null}
          />

          <Button
            type="submit"
            anchoCompleto
            cargando={enviando}
            disabled={!cumpleTodo || !coinciden}
          >
            Guardar contraseña
          </Button>

          <div className={estilos.acciones}>
            <button type="button" className={estilos.enlace} onClick={() => void salir()}>
              Cerrar sesión
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
